import type { AxiosInstance } from "axios";
import { ArrowDown, ArrowUp, ArrowUpDown, Clock3, ExternalLink, MapPin, MousePointerClick, Receipt, Route, Search, ShoppingCart } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { DateRangePicker } from "../components/DateRangePicker.js";
import { FormulaList, HighlightCard, HighlightGrid, noteText } from "../components/ReportGuide/GuideContent.js";
import { ReportGuideAccordion } from "../components/ReportGuide/ReportGuideAccordion.js";
import { ReportGuideToggleButton } from "../components/ReportGuide/ReportGuideToggleButton.js";
import { Button } from "../components/ui/button.js";
import { Card } from "../components/ui/card.js";
import { Input } from "../components/ui/input.js";
import { Label } from "../components/ui/label.js";
import { Select } from "../components/ui/select.js";
import { Skeleton } from "../components/ui/skeleton.js";
import { apiErrorMessage } from "../lib/api.js";
import { cn } from "../lib/cn.js";
import { TableResizeHandle, useTableResize } from "../components/TableResizeHandle.js";
import { daysBefore, rangeProblem, toLocalDate, type DateRange } from "../lib/dates.js";
import {
  fetchJourneyOrders,
  formatConversionTime,
  journeyMoney,
  type CustomerJourneyOrdersResponse,
  type JourneySortKey,
} from "../lib/customer-journey.js";
import { PAGE_SIZES } from "../lib/reports.js";

/**
 * Customer Journey & Orders.
 * Ported from legacy `ecom_code` (`CustomerJourneyOrders.jsx`) — the STANDALONE page:
 * search/sort/pagination/date-range. The same journey list also opens as a modal from the
 * Purchases cells of Source / Meta / Google Attribution (`CustomerJourneyModal.tsx`), scoped
 * to the orders behind the clicked number — see `customer-journey-orders.ts` in `packages/ch`.
 *
 * COLUMN ORDER AND THE 4 SUMMARY CARDS MATCH `ecom_code` EXACTLY (owner request,
 * 2026-09-18): Order Name, Email, Date, Order value, Discount, Order status, Financial
 * status, User type, Total ads, Unique ads, Conversion time, Session count, Touch
 * points, First Touch, First Touch Campaign, Last Touch, Last Touch Campaign, Location,
 * Product Detail, Total Items, Total Quantity, Order Source — then Total Orders / Total
 * Revenue / Avg Conversion Time / Total Ad Touchpoints as cards above the table, the
 * same 4 (of legacy's 5 — Total Discounts wasn't asked for) legacy shows.
 *
 * EMAIL AND LOCATION ARE THE REAL VALUES, NOT MASKED AND NOT HASHED. `ecomally_platform`'s
 * ClickHouse never stores raw PII, so these two columns are filled by a
 * live cross-database join to Postgres in the API's service layer — owner decision,
 * option 1, 2026-09-18, after a full walk-through of what that surfaces and what it
 * takes to do responsibly. An order whose customer has since been GDPR-redacted (or was
 * never captured) shows blank, not an error — the redaction executes as a real Postgres
 * delete, so there is nothing left to join to.
 */

type SortKey = JourneySortKey;

const COLUMN_COUNT = 22;

function defaultJourneyRange(today: Date = new Date()): DateRange {
  return { from: toLocalDate(daysBefore(today, 6)), to: toLocalDate(today) };
}

const money = journeyMoney;

/**
 * The colour-coded pill scheme below is ported verbatim from legacy's own
 * `getChannelBadge`/`getFulfillmentBadge`/`getFinancialBadge` (`CustomerJourneyOrders.
 * jsx` lines 49-107) — exact hex values, exact matching order — not this platform's
 * generic `Badge` variants, per owner request (2026-09-18): "follow the color UI of
 * ecom_code". `formatTouchpointLabel`'s own long-tail fallback (an AI-assistant
 * referral, a niche source the classifier below has no branch for) is ported too, for
 * the touch values the ClickHouse query passes through raw rather than classifying.
 */
interface PillColors {
  readonly bg: string;
  readonly text: string;
  readonly border: string;
  readonly label: string;
}

function formatTouchpointLabel(source: string): string {
  if (source === "") return "Direct";
  const c = source.toLowerCase();
  if (c.includes("chatgpt")) return "ChatGPT";
  if (c.includes("perplexity")) return "Perplexity";
  if (c.includes("kwikengage")) return "Kwikengage";
  if (c.includes("kwikchat")) return "Kwikchat";
  if (c.includes("whatmore")) return "Whatmore";
  if (c.includes("nitro")) return "NitroRewards";
  // Every channel the ClickHouse query itself already classifies (Google/Meta
  // Organic/Ads, Instagram, Direct) arrives here pre-formatted — this only needs to
  // title-case whatever came through the query's own raw fallback branches.
  return source.charAt(0).toUpperCase() + source.slice(1);
}

function channelBadge(channel: string): PillColors {
  const c = channel.toLowerCase();
  if (c.includes("google") && c.includes("organic")) return { bg: "#f0fdf4", text: "#15803d", border: "#bbf7d0", label: "Google Organic" };
  if (c.includes("meta") && c.includes("organic")) return { bg: "#f5f3ff", text: "#7c3aed", border: "#ddd6fe", label: "Meta Organic" };
  // Google Ads: Warm amber/gold badge (#fffbeb, #b45309, #fde68a) so it clearly contrasts with Google Organic (green),
  // mirroring the distinct coloring of Facebook Ads (blue), Meta Organic (purple), and Instagram (pink).
  if (
    (c.includes("google") && c.includes("ads")) ||
    c.includes("google-ads") ||
    (c.includes("google") && (c.includes("cpc") || c.includes("pmax")))
  ) {
    return { bg: "#fffbeb", text: "#b45309", border: "#fde68a", label: "Google Ads" };
  }
  if (c.includes("facebook") || c.includes("meta")) return { bg: "#eef2ff", text: "#3b82f6", border: "#bfdbfe", label: "Facebook Ads" };
  if (c.includes("instagram")) return { bg: "#fdf2f8", text: "#ec4899", border: "#fbcfe8", label: "Instagram" };
  if (c.includes("google")) return { bg: "#f0fdf4", text: "#15803d", border: "#bbf7d0", label: "Google Organic" };
  if (c.includes("direct")) return { bg: "#fff7ed", text: "#f97316", border: "#ffedd5", label: "Direct" };
  if (c.includes("organic")) return { bg: "#f0fdf4", text: "#10b981", border: "#bbf7d0", label: "Organic" };
  if (c.includes("chatgpt")) return { bg: "#f0fdfa", text: "#0d9488", border: "#99f6e4", label: "ChatGPT" };
  return { bg: "#f8fafc", text: "#64748b", border: "#e2e8f0", label: formatTouchpointLabel(channel) || "Other" };
}

/**
 * Ported EXACTLY as legacy has it, quirks included: "Fulfilled", "Cancelled",
 * "Partially Fulfilled" and "Restocked" all fall through to the same neutral grey —
 * legacy's own function never special-cased them (its "Delivered"/"In Transit" states
 * assume fulfillment tracking this platform doesn't have — see the report's own file
 * header). Only "Unfulfilled" gets its own colour.
 */
function fulfillmentBadge(status: string): PillColors {
  const s = status.toLowerCase();
  if (s.includes("delivered")) return { bg: "#f0fdf4", text: "#16a34a", border: "#bbf7d0", label: "Delivered" };
  if (s.includes("transit") || s.includes("shipped")) return { bg: "#f0f9ff", text: "#0284c7", border: "#bae6fd", label: "In Transit" };
  if (s.includes("unfulfilled") || s.includes("pending")) return { bg: "#fefce8", text: "#ca8a04", border: "#fef08a", label: "Unfulfilled" };
  return { bg: "#f8fafc", text: "#64748b", border: "#e2e8f0", label: status || "In Transit" };
}

function financialBadge(status: string): PillColors {
  const s = status.toLowerCase();
  if (s.includes("paid")) return { bg: "#ecfdf5", text: "#059669", border: "#a7f3d0", label: "Paid" };
  if (s.includes("pending") || s.includes("authorized")) return { bg: "#fffbeb", text: "#d97706", border: "#fde68a", label: "Pending" };
  if (s.includes("refund") || s.includes("void")) return { bg: "#fef2f2", text: "#dc2626", border: "#fecaca", label: "Refunded" };
  return { bg: "#f8fafc", text: "#64748b", border: "#e2e8f0", label: status || "Paid" };
}

function userTypeBadge(isNew: boolean): PillColors {
  return isNew
    ? { bg: "#ecfeff", text: "#0891b2", border: "#a5f3fc", label: "New" }
    : { bg: "#f5f3ff", text: "#7c3aed", border: "#ddd6fe", label: "Returning" };
}

/** `rounded-full` pill — Order Status / Financial Status / User Type, matching legacy's own shape for those three. */
function StatusPill({ colors }: { readonly colors: PillColors }): React.JSX.Element {
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap"
      style={{ backgroundColor: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }}
    >
      {colors.label}
    </span>
  );
}

/** `rounded` (not full) pill — First Touch / Last Touch, matching legacy's own shape for the channel columns specifically. */
function ChannelPill({ channel }: { readonly channel: string }): React.JSX.Element {
  const colors = channelBadge(channel);
  return (
    <span
      className="inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold whitespace-nowrap"
      style={{ backgroundColor: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }}
    >
      {colors.label}
    </span>
  );
}

/**
 * Touch Points — indigo path text with the same portal/fixed-position hover tooltip
 * technique `AttributionModelAnalysisScreen.tsx`'s `ColumnTooltip` established (plain
 * absolute positioning is clipped by this table's own horizontal scroll), showing each
 * step as a dark chip, matching legacy's own tooltip content exactly.
 */
function TouchpointsCell({ sources }: { readonly sources: readonly string[] }): React.JSX.Element {
  const triggerRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const labels = (sources.length > 0 ? sources : ["Direct"]).map(formatTouchpointLabel);

  const show = (): void => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect === undefined) return;
    setCoords({ top: rect.bottom + 8, left: rect.left });
  };

  return (
    <span className="inline-block">
      <div
        ref={triggerRef}
        onMouseEnter={show}
        onMouseLeave={() => setCoords(null)}
        className="max-w-56 cursor-pointer truncate text-xs font-medium whitespace-nowrap text-indigo-600 hover:text-indigo-800"
      >
        {labels.join(" > ")}
      </div>
      {coords !== null &&
        createPortal(
          <div
            role="tooltip"
            style={{ position: "fixed", top: coords.top, left: coords.left }}
            className="pointer-events-none z-50 max-w-xs rounded-md bg-slate-800 px-2.5 py-1.5 shadow-lg"
          >
            <div className="mb-1 text-xs font-semibold text-slate-200">Customer Touchpoint Journey:</div>
            <div className="flex flex-wrap items-center gap-1">
              {labels.map((step, i) => (
                <span key={i} className="flex items-center gap-1">
                  <span className="rounded border border-slate-700 bg-slate-800 px-1.5 py-0.5 text-xs font-medium text-indigo-200">{step}</span>
                  {i < labels.length - 1 && <span className="font-bold text-slate-400">›</span>}
                </span>
              ))}
            </div>
          </div>,
          document.body,
        )}
    </span>
  );
}

/** Product Detail — Shopify's own order confirmation/tracking link when present (legacy's own behaviour), a plain product list otherwise. */
function ProductDetailCell({ value }: { readonly value: string }): React.JSX.Element {
  const isUrl = value.startsWith("http://") || value.startsWith("https://");
  if (isUrl) {
    return (
      <a
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(event) => event.stopPropagation()}
        title={value}
        className="flex max-w-56 items-center gap-1 truncate font-mono text-xs text-blue-600 hover:text-blue-800 hover:underline"
      >
        <ExternalLink className="size-3 shrink-0" />
        <span className="truncate">{value}</span>
      </a>
    );
  }
  return (
    <span className="block max-w-56 truncate text-xs font-medium" title={value}>
      {value}
    </span>
  );
}

function KpiCard({
  icon,
  label,
  value,
}: {
  readonly icon: React.ReactNode;
  readonly label: string;
  readonly value: string | null;
}): React.JSX.Element {
  return (
    <Card className="gap-2 p-4">
      <div className="text-muted-foreground flex items-center justify-between text-xs font-medium tracking-wide uppercase">
        <span>{label}</span>
        <span className="text-primary">{icon}</span>
      </div>
      {value === null ? <Skeleton className="h-7 w-24" /> : <div className="text-foreground text-2xl font-bold tracking-tight tabular-nums">{value}</div>}
    </Card>
  );
}

export function CustomerJourneyOrdersScreen({
  client,
  tenantId,
  storeName,
}: {
  readonly client: AxiosInstance;
  readonly tenantId: string;
  readonly storeName: string;
}): React.JSX.Element {
  const [range, setRange] = useState<DateRange>(() => defaultJourneyRange());
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<SortKey>("date");
  const [direction, setDirection] = useState<"asc" | "desc">("desc");
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);

  const [data, setData] = useState<CustomerJourneyOrdersResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showGuideSection, setShowGuideSection] = useState(false);
  const { colWidths, startResize } = useTableResize([
    140, 200, 165, 120, 105, 130, 130, 100, 95, 100, 145, 110, 260, 135, 190, 135, 190, 170, 150, 105, 110, 120,
  ]);

  const problem = useMemo(() => rangeProblem(range), [range]);

  const load = useCallback(async () => {
    if (problem !== null) return;
    setLoading(true);
    setError(null);
    try {
      setData(await fetchJourneyOrders(client, { from: range.from, to: range.to, search, sortBy, direction, limit, offset }));
    } catch (caught) {
      setError(apiErrorMessage(caught, "Could not load customer journey & orders. Please verify your connection."));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [client, range.from, range.to, search, sortBy, direction, limit, offset, problem]);

  useEffect(() => {
    void load();
  }, [load, tenantId]);

  const handleSortToggle = (key: SortKey): void => {
    setOffset(0);
    if (sortBy !== key) {
      setSortBy(key);
      setDirection("desc");
    } else {
      setDirection(direction === "desc" ? "asc" : "desc");
    }
  };

  const orders = data?.orders ?? [];
  const isEmpty = !loading && orders.length === 0;
  const summary = data?.summary ?? null;

  return (
    <section className="flex min-w-0 flex-col gap-5">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2.5 text-2xl font-bold tracking-tight">
            <Route className="text-primary size-6" />
            Customer Journey & Orders
            <ReportGuideToggleButton
              showGuide={showGuideSection}
              onToggle={() => setShowGuideSection((current) => !current)}
              showLabel="Click to view Customer Journey Guide"
              hideLabel="Hide Guide"
            />
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Every order in this range, with its full multi-touch journey — sessions, ad touches, first/last touch and conversion time.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <DateRangePicker
            value={range}
            onApply={(next) => {
              setOffset(0);
              setRange(next);
            }}
          />
        </div>
      </header>

      <ReportGuideAccordion
        showGuide={showGuideSection}
        whatTitle="What Customer Journey & Orders shows"
        whatBadge="Report Guide"
        whatContent={
          <div className={noteText}>
            <p className="m-0">
              One row per order, with the full path that led to it — how many sessions it took, how many of those
              sessions touched an ad, and which channel opened and closed the journey.
            </p>
            <HighlightGrid cols={3}>
              <HighlightCard tone="blue" title="1. First & Last Touch" icon={<Route className="size-3.5" />}>
                The channel and campaign that started the journey, and the one that closed it — read directly off this
                store's own precomputed touch history, not re-derived per request.
              </HighlightCard>
              <HighlightCard tone="purple" title="2. New vs Returning" icon={<MapPin className="size-3.5" />}>
                "New" is this customer's first order ever; "Returning" is every order after it — a lifetime count, not
                scoped to the date range on screen.
              </HighlightCard>
              <HighlightCard tone="emerald" title="3. Conversion Time" icon={<Search className="size-3.5" />}>
                From the very first touch this customer's journey has on record to the moment they purchased.
              </HighlightCard>
            </HighlightGrid>
          </div>
        }
        howTitle="What's shown, and what's deferred"
        howBadge="Scope"
        howContent={
          <div className={noteText}>
            <FormulaList>
              <li>
                <strong>Email & location</strong> are this order's real, unmasked customer record — a live lookup
                against this store's own customer data, not a hash. A blank means this platform has no record for
                that customer (never captured, or since erased under a GDPR request).
              </li>
              <li>
                <strong>Drilling into a specific attribution model, campaign, ad set or ad</strong> is not on this
                page — that filter belongs to the journey view opened FROM another report, a separate piece of work.
              </li>
            </FormulaList>
          </div>
        }
      />

      {problem !== null && (
        <p className="text-destructive text-sm" role="alert">
          {problem}
        </p>
      )}
      {error !== null && (
        <p className="text-destructive text-sm" role="alert">
          {error}
        </p>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard icon={<ShoppingCart className="size-4" />} label="Total Orders" value={summary === null ? null : summary.totalOrders.toLocaleString("en-IN")} />
        <KpiCard icon={<Receipt className="size-4" />} label="Total Revenue" value={summary === null ? null : money(summary.totalRevenue)} />
        <KpiCard icon={<Clock3 className="size-4" />} label="Avg Conversion Time" value={summary === null ? null : formatConversionTime(summary.avgConversionSeconds)} />
        <KpiCard icon={<MousePointerClick className="size-4" />} label="Total Ad Touchpoints" value={summary === null ? null : summary.totalAdTouchpoints.toLocaleString("en-IN")} />
      </div>

      <div className="bg-card min-w-0 overflow-hidden rounded-xl border shadow-sm">
        <div className="flex flex-wrap items-center gap-3 border-b p-3">
          <div className="relative flex-1 sm:max-w-xs">
            <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
            <Input
              type="search"
              placeholder="Search order # or product…"
              aria-label="Search orders"
              className="h-9 pl-8"
              value={search}
              onChange={(event) => {
                setOffset(0);
                setSearch(event.target.value);
              }}
            />
          </div>
        </div>

        <div className="max-h-[min(70svh,48rem)] overflow-auto">
          <table className="table-fixed border-separate border-spacing-0 text-sm" style={{ width: `${colWidths.reduce((a, b) => a + b, 0)}px`, minWidth: "100%" }}>
            <colgroup>
              {colWidths.map((w, i) => (
                <col key={i} style={{ width: `${w}px` }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <PlainHeader label="Order Name" onResize={(e) => startResize(0, e)} />
                <PlainHeader label="Email" onResize={(e) => startResize(1, e)} />
                <SortableHeader label="Date" columnKey="date" sortBy={sortBy} direction={direction} onToggle={handleSortToggle} onResize={(e) => startResize(2, e)} />
                <SortableHeader label="Order Value" columnKey="order_value" sortBy={sortBy} direction={direction} onToggle={handleSortToggle} align="right" onResize={(e) => startResize(3, e)} />
                <SortableHeader label="Discount" columnKey="discount" sortBy={sortBy} direction={direction} onToggle={handleSortToggle} align="right" onResize={(e) => startResize(4, e)} />
                <PlainHeader label="Order Status" onResize={(e) => startResize(5, e)} />
                <PlainHeader label="Financial Status" onResize={(e) => startResize(6, e)} />
                <PlainHeader label="User Type" onResize={(e) => startResize(7, e)} />
                <SortableHeader label="Total Ads" columnKey="total_ads" sortBy={sortBy} direction={direction} onToggle={handleSortToggle} align="right" onResize={(e) => startResize(8, e)} />
                <SortableHeader label="Unique Ads" columnKey="unique_ads" sortBy={sortBy} direction={direction} onToggle={handleSortToggle} align="right" onResize={(e) => startResize(9, e)} />
                <SortableHeader label="Conversion Time" columnKey="conversion_time" sortBy={sortBy} direction={direction} onToggle={handleSortToggle} align="right" onResize={(e) => startResize(10, e)} />
                <SortableHeader label="Session Count" columnKey="session_count" sortBy={sortBy} direction={direction} onToggle={handleSortToggle} align="right" onResize={(e) => startResize(11, e)} />
                <PlainHeader label="Touch Points" onResize={(e) => startResize(12, e)} />
                <PlainHeader label="First Touch" onResize={(e) => startResize(13, e)} />
                <PlainHeader label="First Touch Campaign" onResize={(e) => startResize(14, e)} />
                <PlainHeader label="Last Touch" onResize={(e) => startResize(15, e)} />
                <PlainHeader label="Last Touch Campaign" onResize={(e) => startResize(16, e)} />
                <PlainHeader label="Location" onResize={(e) => startResize(17, e)} />
                <PlainHeader label="Product Detail" onResize={(e) => startResize(18, e)} />
                <SortableHeader label="Total Items" columnKey="total_items" sortBy={sortBy} direction={direction} onToggle={handleSortToggle} align="right" onResize={(e) => startResize(19, e)} />
                <SortableHeader label="Total Quantity" columnKey="total_quantity" sortBy={sortBy} direction={direction} onToggle={handleSortToggle} align="right" onResize={(e) => startResize(20, e)} />
                <PlainHeader label="Order Source" onResize={(e) => startResize(21, e)} />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 8 }, (_, i) => (
                  <tr key={i} className={i % 2 === 1 ? "row-odd" : "row-even"}>
                    {Array.from({ length: COLUMN_COUNT }, (__, j) => (
                      <td key={j} className="border-b px-3 py-3">
                        <Skeleton className="h-4 w-16" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : isEmpty ? (
                <tr>
                  <td colSpan={COLUMN_COUNT} className="text-muted-foreground px-4 py-14 text-center text-sm">
                    No orders found for {storeName} in this date range.
                  </td>
                </tr>
              ) : (
                orders.map((order, i) => (
                  <tr key={order.order_id} className={cn(i % 2 === 1 ? "row-odd" : "row-even", "hover:bg-muted/40 transition-colors")}>
                    <td className="border-b px-3 py-2.5 font-medium text-foreground">{order.order_number}</td>
                    <td className="border-b px-3 py-2.5">
                      <span className="block max-w-52 truncate" title={order.email || "No record"}>
                        {order.email || <span className="text-muted-foreground italic">No record</span>}
                      </span>
                    </td>
                    <td className="border-b px-3 py-2.5 whitespace-nowrap text-muted-foreground">
                      {new Date(order.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                    </td>
                    <td className="border-b px-3 py-2.5 text-right font-medium text-foreground">{money(order.order_value)}</td>
                    <td className="border-b px-3 py-2.5 text-right text-muted-foreground">{order.discount > 0 ? money(order.discount) : "—"}</td>
                    <td className="border-b px-3 py-2.5">
                      <StatusPill colors={fulfillmentBadge(order.order_status)} />
                    </td>
                    <td className="border-b px-3 py-2.5">
                      <StatusPill colors={financialBadge(order.financial_status)} />
                    </td>
                    <td className="border-b px-3 py-2.5">
                      <StatusPill colors={userTypeBadge(order.user_type === "New")} />
                    </td>
                    <td className="border-b px-3 py-2.5 text-right">{order.total_ads}</td>
                    <td className="border-b px-3 py-2.5 text-right">{order.unique_ads}</td>
                    <td className="border-b px-3 py-2.5 text-right whitespace-nowrap">{formatConversionTime(order.conversion_time_seconds)}</td>
                    <td className="border-b px-3 py-2.5 text-right">{order.session_count}</td>
                    <td className="border-b px-3 py-2.5">
                      <TouchpointsCell sources={order.touchpoint_sources} />
                    </td>
                    <td className="border-b px-3 py-2.5">
                      <ChannelPill channel={order.first_touch_source} />
                    </td>
                    <td className="border-b px-3 py-2.5">
                      <span className="text-muted-foreground block max-w-40 truncate" title={order.first_touch_campaign}>
                        {order.first_touch_campaign}
                      </span>
                    </td>
                    <td className="border-b px-3 py-2.5">
                      <ChannelPill channel={order.last_touch_source} />
                    </td>
                    <td className="border-b px-3 py-2.5">
                      <span className="text-muted-foreground block max-w-40 truncate" title={order.last_touch_campaign}>
                        {order.last_touch_campaign}
                      </span>
                    </td>
                    <td className="border-b px-3 py-2.5">
                      {order.location === "" ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <div className="text-muted-foreground flex items-center gap-1">
                          <MapPin className="size-3 shrink-0" />
                          <span className="max-w-48 truncate">{order.location}</span>
                        </div>
                      )}
                    </td>
                    <td className="border-b px-3 py-2.5">
                      <ProductDetailCell value={order.product_detail} />
                    </td>
                    <td className="border-b px-3 py-2.5 text-right">{order.total_items}</td>
                    <td className="border-b px-3 py-2.5 text-right">{order.total_quantity}</td>
                    <td className="border-b px-3 py-2.5">
                      <StatusPill colors={{ bg: "#f8fafc", text: "#64748b", border: "#e2e8f0", label: order.order_source }} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {(offset > 0 || (data !== null && offset + orders.length < data.totalOrderCount)) && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <nav aria-label="Pagination" className="flex items-center gap-3">
            <Button variant="outline" size="sm" disabled={offset === 0 || loading} onClick={() => setOffset(Math.max(0, offset - limit))}>
              Previous
            </Button>
            <span className="text-muted-foreground text-xs">
              Rows {offset + 1}–{offset + orders.length}
              {data !== null && ` of ${data.totalOrderCount.toLocaleString("en-IN")}`}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={data === null || offset + limit >= data.totalOrderCount || loading}
              onClick={() => setOffset(offset + limit)}
            >
              Next
            </Button>
          </nav>

          <div className="flex items-center gap-2">
            <Label htmlFor="journey-rows" className="text-muted-foreground text-xs">
              Rows
            </Label>
            <Select
              id="journey-rows"
              className="h-9 w-[5.5rem]"
              value={limit}
              onChange={(event) => {
                setOffset(0);
                setLimit(Number(event.target.value));
              }}
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </Select>
          </div>
        </div>
      )}
    </section>
  );
}

function SortIndicator({ active, direction }: { readonly active: boolean; readonly direction: "asc" | "desc" }): React.JSX.Element {
  if (!active) return <ArrowUpDown className="size-3.5 text-muted-foreground/40 shrink-0" />;
  return direction === "asc" ? <ArrowUp className="size-3.5 text-primary shrink-0" /> : <ArrowDown className="size-3.5 text-primary shrink-0" />;
}

function PlainHeader({
  label,
  sticky = false,
  onResize,
}: {
  readonly label: string;
  readonly sticky?: boolean;
  readonly onResize?: (event: React.MouseEvent) => void;
}): React.JSX.Element {
  return (
    <th
      className={cn(
        "row-head text-foreground sticky top-0 z-20 h-11 border-b border-r border-border px-3 text-left text-[13px] font-semibold whitespace-nowrap relative",
        sticky && "left-0 z-30",
      )}
    >
      <span className="pr-1">{label}</span>
      {onResize && <TableResizeHandle onResize={onResize} />}
    </th>
  );
}

function SortableHeader({
  label,
  columnKey,
  sortBy,
  direction,
  onToggle,
  align = "left",
  sticky = false,
  onResize,
}: {
  readonly label: string;
  readonly columnKey: SortKey;
  readonly sortBy: SortKey;
  readonly direction: "asc" | "desc";
  readonly onToggle: (key: SortKey) => void;
  readonly align?: "left" | "right";
  readonly sticky?: boolean;
  readonly onResize?: (event: React.MouseEvent) => void;
}): React.JSX.Element {
  const active = sortBy === columnKey;
  return (
    <th
      onClick={() => onToggle(columnKey)}
      className={cn(
        "row-head text-foreground sticky top-0 z-20 h-11 border-b border-r border-border px-3 text-[13px] font-semibold cursor-pointer select-none whitespace-nowrap relative",
        align === "right" ? "text-right" : "text-left",
        sticky && "left-0 z-30",
      )}
    >
      <div className={cn("flex items-center gap-1.5 pr-1", align === "right" && "justify-end")}>
        <span>{label}</span>
        <SortIndicator active={active} direction={direction} />
      </div>
      {onResize && <TableResizeHandle onResize={onResize} />}
    </th>
  );
}
